import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhookService, WebhookConfig } from '@/services/webhook.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { WEBHOOK_SETTINGS_PERMISSION } from '@/constants/constant-permissions';
import {
  Webhook,
  Plus,
  Edit2,
  Trash2,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  Shield,
  Eye,
  EyeOff,
  Search,
  Zap,
  Globe,
  Key,
  Code,
  TestTube2,
  History,
  X,
  Clock,
  Send,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect from '@/components/ui/SimpleSelect';
import WebhookModal from '@/components/settings/CreateWebhookModal';

export default function WebhookSettings() {
  usePermissionRouteAccess([
    WEBHOOK_SETTINGS_PERMISSION.ADMIN_WEBHOOK_SETTINGS_READ,
    WEBHOOK_SETTINGS_PERMISSION.ADMIN_WEBHOOK_SETTINGS_WRITE,
  ]);
  const canWrite = useHasPermission([WEBHOOK_SETTINGS_PERMISSION.ADMIN_WEBHOOK_SETTINGS_WRITE]);
  const queryClient = useQueryClient();

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // UI state
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookConfig | null>(null);
  const [showApiValue, setShowApiValue] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'error'>('all');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<{ id: string; name: string } | null>(null);
  const [webhookHistory, setWebhookHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);


  // API Queries
  const { data: webhooksData, isLoading } = useQuery({
    queryKey: ['webhooks', page, pageSize],
    queryFn: () => webhookService.getWebhooks({
      skip: (page - 1) * pageSize,
      limit: pageSize
    })
  });

  const { data: triggerEventsData } = useQuery({
    queryKey: ['webhookTriggerEvents'],
    queryFn: () => webhookService.getTriggerEvents()
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<WebhookConfig>) => webhookService.createWebhook(data),
    onSuccess: () => {
      toast.success('Webhook created successfully!');
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setShowWebhookModal(false);
      setSelectedWebhook(null);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Failed to create webhook';
      toast.error(errorMessage);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<WebhookConfig> }) =>
      webhookService.updateWebhook(id, data),
    onSuccess: () => {
      toast.success('Webhook updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setShowWebhookModal(false);
      setSelectedWebhook(null);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Failed to update webhook';
      toast.error(errorMessage);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => webhookService.deleteWebhook(id),
    onSuccess: () => {
      toast.success('Webhook deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete webhook');
    }
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => webhookService.testWebhook(id),
    onSuccess: () => {
      toast.success('Test webhook sent successfully!');
      setShowTestModal(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to test webhook');
    }
  });

  // Helper functions
  const webhooks: WebhookConfig[] = webhooksData?.list || [];
  const totalCount = webhooksData?.totalCount || 0;

  // Client-side filtering (for current page)
  const filteredWebhooks = webhooks.filter(webhook => {
    const matchesSearch =
      webhook.webhook_url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.webhook_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.webhook_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.webhook_trigger_events?.some(event => event.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && webhook.is_active) ||
      (filterStatus === 'inactive' && !webhook.is_active) ||
      (filterStatus === 'error' && webhook.failure_count && webhook.failure_count > 0);

    return matchesSearch && matchesFilter;
  });

  // Calculate metrics from ALL webhooks (not filtered)
  const metrics = {
    total: totalCount,
    active: webhooks.filter(w => w.is_active).length,
    failed: webhooks.filter(w => w.failure_count && w.failure_count > 0).length,
    total_calls: webhooks.reduce((sum, w) => sum + (w.total_calls || 0), 0)
  };

  const getStatusIcon = (webhook: WebhookConfig) => {
    if (webhook.is_active) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    if (webhook.failure_count && webhook.failure_count > 0) {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    return <XCircle className="w-4 h-4 text-gray-400" />;
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-700';
      case 'POST': return 'bg-green-100 text-green-700';
      case 'PUT': return 'bg-yellow-100 text-yellow-700';
      case 'PATCH': return 'bg-orange-100 text-orange-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const onSubmit = async (data: any) => {
    const apiData: any = {
      webhook_name: data.webhook_name,
      webhook_url: data.webhook_url,
      webhook_trigger_events: data.webhook_trigger_events,
      webhook_method: data.webhook_method,
      webhook_content_type: data.webhook_content_type,
      webhook_priority: data.webhook_priority,
      webhook_api_key_format: (data.webhook_api_key_format === 'header' ? 'HEADER' :
                               data.webhook_api_key_format === 'query' ? 'QUERY_STRING' : 'BODY') as any,
      webhook_api_key: data.webhook_api_key,
      webhook_description: data.webhook_description,
      is_active: data.is_active,
      retry_enabled: data.retry_enabled,
      retry_max_attempts: data.retry_max_attempts,
      retry_backoff_seconds: data.retry_backoff_seconds,
      timeout_seconds: data.timeout_seconds,
      include_signature: data.include_signature,
      signature_header: data.signature_header
    };

    // Only include encrypted fields if they have a value (not empty or the encrypted marker)
    if (data.webhook_api_value && data.webhook_api_value.trim() !== '' && data.webhook_api_value !== '***ENCRYPTED***') {
      apiData.webhook_api_value = data.webhook_api_value;
    }

    if (data.webhook_secret && data.webhook_secret.trim() !== '' && data.webhook_secret !== '***ENCRYPTED***') {
      apiData.webhook_secret = data.webhook_secret;
    }

    if (selectedWebhook?._id) {
      await updateMutation.mutateAsync({
        id: selectedWebhook._id,
        data: apiData
      });
    } else {
      await createMutation.mutateAsync(apiData);
    }
  };

  const handleDeleteWebhook = (webhook: WebhookConfig) => {
    setWebhookToDelete({
      id: webhook._id!,
      name: webhook.webhook_name || webhook.webhook_description || 'Webhook'
    });
    setShowDeleteModal(true);
  };

  const openEditModal = (webhook: WebhookConfig) => {
    setSelectedWebhook(webhook);
    setShowWebhookModal(true);
  };

  const openCreateModal = () => {
    setSelectedWebhook(null);
    setShowWebhookModal(true);
  };

  const toggleApiValueVisibility = (id: string) => {
    setShowApiValue(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const testWebhook = (webhook: WebhookConfig) => {
    setSelectedWebhook(webhook);
    setShowTestModal(true);
  };

  const viewHistory = async (webhook: WebhookConfig) => {
    setSelectedWebhook(webhook);
    setShowHistoryModal(true);
    setIsLoadingHistory(true);

    if (webhook._id) {
      try {
        const history = await webhookService.getWebhookHistory(webhook._id);
        setWebhookHistory(history?.items || history || []);
      } catch (error: any) {
        toast.error(error.message || 'Failed to load webhook history');
        setWebhookHistory([]);
      } finally {
        setIsLoadingHistory(false);
      }
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const triggerEvents: any[] = triggerEventsData || [];

  const getTriggerEventLabel = (eventValue: string) => {
    const event = triggerEvents.find(e => e.value === eventValue);
    return event?.label || eventValue;
  };

  const closeWebhookModal = () => {
    setShowWebhookModal(false);
    setSelectedWebhook(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Webhook Configuration</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage webhooks to integrate with external services and automate workflows
          </p>
        </div>
        <button
          onClick={() => canWrite && openCreateModal()}
          disabled={!canWrite}
          title={!canWrite ? 'You do not have permission' : undefined}
          className={`btn btn-primary flex items-center gap-2${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
        >
          <Plus className="w-4 h-4" />
          Create Webhook
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Webhooks</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.total}</p>
            </div>
            <Webhook className="w-8 h-8 text-primary-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{metrics.active}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-brand-green-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{metrics.failed}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Calls</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.total_calls.toLocaleString()}
              </p>
            </div>
            <Activity className="w-8 h-8 text-brand-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search webhooks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <div className="w-44 flex-shrink-0">
              <SimpleSelect
                value={filterStatus}
                onChange={(value) => setFilterStatus((value || 'all') as any)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'error', label: 'Has Errors' },
                ]}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Webhooks List with Skeleton Loader */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <CardSkeleton key={i} lines={4} className="h-48" />
          ))}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {filteredWebhooks.map((webhook) => (
              <div key={webhook._id || webhook.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-50 rounded-lg">
                        <Zap className="w-6 h-6 text-primary-600" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-gray-900">
                            {webhook.webhook_name || webhook.webhook_description || 'Webhook Endpoint'}
                          </h3>
                          {getStatusIcon(webhook)}
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getMethodColor(webhook.webhook_method)}`}>
                            {webhook.webhook_method}
                          </span>
                          {webhook.webhook_url?.startsWith('https://') && (
                            <span title="Secure HTTPS"><Shield className="w-4 h-4 text-green-600" /></span>
                          )}
                        </div>

                        {webhook.webhook_description && (
                          <p className="text-sm text-gray-600 mb-3">{webhook.webhook_description}</p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Trigger Events</p>
                            <div className="flex flex-wrap gap-1">
                              {webhook.webhook_trigger_events?.map((event, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs font-medium bg-brand-blue-100 text-brand-blue-700 px-2 py-1 rounded flex items-center gap-1"
                                >
                                  <Activity className="w-3 h-3" />
                                  {getTriggerEventLabel(event)}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500 mb-1">Endpoint URL</p>
                            <div className="flex items-center gap-2">
                              <Globe className="w-3 h-3 text-gray-400" />
                              <p className="text-sm font-mono text-gray-900 truncate max-w-xs" title={webhook.webhook_url}>
                                {webhook.webhook_url}
                              </p>
                              <button
                                onClick={() => copyToClipboard(webhook.webhook_url)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500 mb-1">API Key Configuration</p>
                            <div className="flex items-center gap-2">
                              <Key className="w-3 h-3 text-gray-400" />
                              <p className="text-sm text-gray-900">
                                <span className="font-medium">{webhook.webhook_api_key_format?.toLowerCase() || 'header'}:</span> {webhook.webhook_api_key || 'None'}
                              </p>
                            </div>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500 mb-1">API Value</p>
                            <div className="flex items-center gap-2">
                              <Code className="w-3 h-3 text-gray-400" />
                              <p className="text-sm font-mono text-gray-900">
                                {showApiValue[webhook._id!]
                                  ? webhook.webhook_api_value?.substring(0, 20) + '...'
                                  : '••••••••••••'}
                              </p>
                              <button
                                onClick={() => toggleApiValueVisibility(webhook._id!)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {showApiValue[webhook._id!] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last triggered: {formatDate(webhook.last_triggered_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {webhook.total_calls || 0} calls
                          </span>
                          {webhook.success_rate !== undefined && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              {webhook.success_rate.toFixed(1)}% success rate
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => testWebhook(webhook)}
                      disabled={!webhook.is_active}
                      className={`p-2 rounded-lg ${
                        webhook.is_active
                          ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                          : 'text-gray-300 cursor-not-allowed opacity-50'
                      }`}
                      title={webhook.is_active ? "Test Webhook" : "Webhook must be active to test"}
                    >
                      <TestTube2 className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => viewHistory(webhook)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="View History"
                    >
                      <History className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => openEditModal(webhook)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDeleteWebhook(webhook)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredWebhooks.length === 0 && (
              <div className="card text-center py-12">
                <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No webhooks found</p>
                <p className="text-sm text-gray-500">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first webhook to get started'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalCount > pageSize && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalCount / pageSize)}
              totalItems={totalCount}
              itemsPerPage={pageSize}
              onPageChange={(newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onItemsPerPageChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
            />
          )}
        </>
      )}

      {/* Webhook Modal */}
      <WebhookModal
        isOpen={showWebhookModal}
        onClose={closeWebhookModal}
        onSubmit={onSubmit}
        webhook={selectedWebhook}
        triggerEvents={triggerEvents}
      />

      {/* Test Webhook Modal */}
      {showTestModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b relative">
              <button
                onClick={() => setShowTestModal(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900 pr-8">Test Webhook</h2>
              <p className="text-sm text-gray-600 mt-1">
                Send a test request to {selectedWebhook.webhook_description || 'webhook endpoint'}
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {!selectedWebhook.is_active && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Webhook is Inactive</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        This webhook is currently inactive. Please activate it before testing.
                      </p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Test Payload</p>
                  <pre className="text-xs text-gray-600 overflow-x-auto">
{JSON.stringify({
  events: selectedWebhook.webhook_trigger_events,
  timestamp: new Date().toISOString(),
  data: {
    test: true,
    message: "This is a test webhook call"
  }
}, null, 2)}
                  </pre>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Send className="w-4 h-4" />
                  <span>This will send a test request to:</span>
                </div>
                <p className="text-sm font-mono bg-gray-100 p-2 rounded">
                  {selectedWebhook.webhook_url}
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowTestModal(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedWebhook._id) {
                    testMutation.mutate(selectedWebhook._id);
                  }
                }}
                className="btn btn-primary flex items-center gap-2"
                disabled={testMutation.isPending || !selectedWebhook.is_active}
                title={!selectedWebhook.is_active ? "Webhook must be active to test" : ""}
              >
                {testMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedWebhook && (
        <div className="fixed inset-0  bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[85vh] overflow-hidden">
            <div className="p-6 border-b relative">
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setWebhookHistory([]);
                }}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900 pr-8">Webhook Execution History</h2>
              <p className="text-sm text-gray-600 mt-1">
                Recent calls for {selectedWebhook.webhook_name || selectedWebhook.webhook_description || 'webhook'}
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(85vh-200px)]">
              {isLoadingHistory ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <CardSkeleton key={i} lines={3} className="h-40" />
                  ))}
                </div>
              ) : webhookHistory.length > 0 ? (
                <div className="space-y-4">
                  {webhookHistory.map((history, idx) => (
                    <div key={history._id || idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            history.status === 'SUCCESS' ? 'bg-green-100' :
                            history.status === 'FAILED' ? 'bg-red-100' :
                            history.status === 'PENDING' ? 'bg-yellow-100' :
                            history.status === 'TIMEOUT' ? 'bg-orange-100' :
                            history.status === 'RETRYING' ? 'bg-blue-100' :
                            'bg-gray-100'
                          }`}>
                            {history.status === 'SUCCESS' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : history.status === 'FAILED' || history.status === 'TIMEOUT' ? (
                              <XCircle className="w-5 h-5 text-red-600" />
                            ) : history.status === 'RETRYING' ? (
                              <Loader2 className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Clock className="w-5 h-5 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                history.status === 'SUCCESS' ? 'bg-green-100 text-green-700' :
                                history.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                                history.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                history.status === 'TIMEOUT' ? 'bg-orange-100 text-orange-700' :
                                history.status === 'RETRYING' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {history.status}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(history.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mt-1">
                              Trigger: <span className="font-medium">{history.trigger_type}</span>
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          {history.http_status_code && (
                            <span className={`text-sm font-mono px-2 py-1 rounded ${
                              history.http_status_code >= 200 && history.http_status_code < 300
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {history.http_status_code}
                            </span>
                          )}
                          {history.response_time_ms && (
                            <p className="text-xs text-gray-500 mt-1">
                              {history.response_time_ms}ms
                            </p>
                          )}
                          {history.retry_attempt !== undefined && history.retry_attempt > 0 && (
                            <p className="text-xs text-orange-600 mt-1">
                              Retry #{history.retry_attempt}
                            </p>
                          )}
                        </div>
                      </div>

                      {history.error_message && (
                        <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                          <p className="text-xs font-medium text-red-700 mb-1">Error Message</p>
                          <p className="text-sm text-red-600">{history.error_message}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        {history.payload && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">Payload</p>
                            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-32">
                              {JSON.stringify(history.payload, null, 2)}
                            </pre>
                          </div>
                        )}

                        {history.response && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">Response</p>
                            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-32">
                              {JSON.stringify(history.response, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No webhook history found</p>
                  <p className="text-sm text-gray-500 mt-2">
                    History will appear here after the webhook is triggered
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setWebhookHistory([]);
                }}
                className="btn btn-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setWebhookToDelete(null);
        }}
        onConfirm={() => {
          if (webhookToDelete) {
            deleteMutation.mutate(webhookToDelete.id);
            setShowDeleteModal(false);
            setWebhookToDelete(null);
          }
        }}
        title="Delete Webhook"
        description="Are you sure you want to delete this webhook? This action cannot be undone."
        itemName={webhookToDelete?.name}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
